import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "../../../redux/store";
import type { AccountRow, ChannelCatalogItem, RequestState } from "../api/connectionHub.types";
import { CHANNEL_SECTION } from "../api/connectionHub.types";
import { REQUEST_STATUS } from "../constants/connectionHub.constants";
import { orderCatalogSections } from "../utils/channelOrder";

export const selectConnectionHubState = (state: RootState) => state.connectionHub;
export const selectConnectionHubSession = (state: RootState) =>
  selectConnectionHubState(state).session;
export const selectConnectionHubIsOpen = (state: RootState) =>
  selectConnectionHubSession(state).isOpen;
export const selectActiveConnectionIpId = (state: RootState) =>
  selectConnectionHubSession(state).activeIpId;
export const selectConnectionCatalog = (state: RootState) =>
  selectConnectionHubState(state).catalog;
export const selectConnectionCatalogStatus = (state: RootState) =>
  selectConnectionCatalog(state).status;
export const selectOrderedConnectionSections = createSelector(
  [selectConnectionCatalog],
  (catalog) => orderCatalogSections(catalog.data)
);
export const selectQuickConnectionSection = createSelector(
  [selectOrderedConnectionSections],
  (sections) => sections.find((section) => section.section === CHANNEL_SECTION.QUICK)
);
export const selectSmartConnectionSection = createSelector(
  [selectOrderedConnectionSections],
  (sections) => sections.find((section) => section.section === CHANNEL_SECTION.SMART)
);
export const selectChannelByIpId = (
  state: RootState,
  ipId: string
): ChannelCatalogItem | undefined =>
  selectConnectionCatalog(state).data?.sections
    .flatMap((section) => section.channels)
    .find((channel) => channel.ipId === ipId);
export const selectSelectedConnectionIpIds = (state: RootState) =>
  selectConnectionHubSession(state).selectedIpIds;
export const selectSelectedConnectionChannels = (state: RootState) => {
  const selected = new Set(selectSelectedConnectionIpIds(state));
  return (
    selectConnectionCatalog(state).data?.sections
      .flatMap((section) => section.channels)
      .filter((channel) => selected.has(channel.ipId)) ?? []
  );
};
const NO_ACCOUNTS: AccountRow[] = [];
const IDLE_ACCOUNT_REQUEST: RequestState = { status: REQUEST_STATUS.IDLE, error: null };

export const selectConnectionAccounts = (state: RootState, ipId: string) =>
  selectConnectionHubState(state).accountsByIpId[ipId] ?? NO_ACCOUNTS;
export const selectConnectionAccountRequest = (state: RootState, ipId: string) =>
  selectConnectionHubState(state).accountRequestsByIpId[ipId] ?? IDLE_ACCOUNT_REQUEST;
export const selectConnectionEmailSuggestions = (state: RootState) =>
  selectConnectionHubState(state).emailSuggestions;
export const selectConnectionCatalogError = (state: RootState) =>
  selectConnectionCatalog(state).error;
export const selectConnectionAccountError = (state: RootState, ipId: string) =>
  selectConnectionAccountRequest(state, ipId).error;

export const selectConnectionSocketState = (state: RootState) => state.connectionHub.socket;
